import { useMemo } from "react";
import BaseDrawer from "../../../components/ui/BaseDrawer";
import WorkOrderStatus from "../../../components/WorkOrderStatus";
import { getLogDetailsByLogId } from "../../../services/api/workOrder";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Avatar, CircularProgress } from "@mui/material";
import BaseTooltip from "../../../components/ui/BaseTooltip";
import { CurrencySymbol } from "../../../utils/enum";

import { formatPhoneNumber } from "../../../utils/helpers";
const truncateHTML = (html, limit = 80) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent || div.innerText || "";
  if (text.length <= limit) return html;
  return text.slice(0, limit) + "...";
};

const formatDateTimeWithTZ = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  return `${formatDate(d)} at ${formattedTime(d)}`;
};

const isValidDate = (value) => {
  if (!value) return false;
  const d = new Date(value);
  return !isNaN(d.getTime());
};

const formatContactValue = (contacts = []) => {
  if (!Array.isArray(contacts) || contacts.length === 0) return "-";
  return contacts
    .map(
      (c) => `
        <div class="space-y-1">
          <div class="font-medium">${c.name || "-"}</div>
          <div class="text-xs text-gray-500">${c.position || "-"}</div>
          <div class="text-xs text-gray-500">${c.email || "-"}</div>
          <div class="text-xs text-gray-500">${formatPhoneNumber(c.phoneNumber) || "-"}</div>
        </div>
      `,
    )
    .join("<hr class='my-1 border-gray-200' />");
};

const formatDate = (dt) => new Date(dt).toLocaleDateString();

const formattedTime = (createdAt) =>
  createdAt
    ? new Date(createdAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "";

const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const formatValue = (label, value) => {
  if (!value) return "-";
  const field = label?.toLowerCase();
  if (field === "contact") return formatContactValue(value);
  if ((field?.includes("date") || field?.includes("time")) && isValidDate(value)) {
    return formatDateTimeWithTZ(value);
  }
  if (typeof value === "string" && ISO_DATE_TIME.test(value) && isValidDate(value)) {
    return formatDateTimeWithTZ(value);
  }
  return value;
};

const SectionHeading = ({ icon, label, trailing }) => (
  <div className="mb-3 flex items-center justify-between">
    <div className="flex items-center gap-2">
      <i className={`fa-light ${icon} text-primary text-sm`} />
      <p className="text-sm font-normal text-gray-700">{label}</p>
    </div>
    {trailing && (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">
        {trailing}
      </span>
    )}
  </div>
);

const WorkOrderHistoryLog = ({ open, onClose, logId }) => {
  const navigate = useNavigate();
  const {
    data: workOrderLogDetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["workOrderLogDetails", logId],
    queryFn: () => getLogDetailsByLogId(logId),
    enabled: !!logId,
  });

  const workOrderLog = workOrderLogDetails?.data;
  const changes = workOrderLog?.metaData?.changes || [];
  const offerTimeline = workOrderLog?.offerTimeline || [];
  const performedById = workOrderLog?.performedBy?.id;
  const showChanges = workOrderLog?.logType !== "OFFER_ASSIGN";
  const displayStatus = workOrderLog?.status ?? workOrderLog?.newStatus;
  const changeSections = useMemo(() => {
    const groups = [];
    (workOrderLog?.metaData?.changes || []).forEach((item) => {
      const section = item.section || "Details";
      const existing = groups.find((g) => g.section === section);
      if (existing) existing.items.push(item);
      else groups.push({ section, items: [item] });
    });
    return groups;
  }, [workOrderLog]);
  const hasStatusChange =
    workOrderLog?.status !== null &&
    workOrderLog?.newStatus &&
    workOrderLog?.status !== workOrderLog?.newStatus;

  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      title="History"
      saveButtonText=""
      isLoading={false}
      enabled={false}
      isShowButton={false}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <CircularProgress size={28} />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400 gap-2">
          <i className="fa-light fa-triangle-exclamation text-2xl text-red-400" />
          <p className="text-sm">Failed to load log details.</p>
        </div>
      ) : (
        <div className="px-3 pb-4">
          <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-slate-50 to-white overflow-hidden">
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h6 className="text-base font-medium text-gray-900 leading-snug">
                    {workOrderLog?.event}
                  </h6>
                  <p className="mt-1 text-xs text-gray-500">
                    {formatDate(workOrderLog?.createdAt)} · {formattedTime(workOrderLog?.createdAt)}
                  </p>
                </div>

                {displayStatus && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    {hasStatusChange ? (
                      <>
                        <WorkOrderStatus
                          status={workOrderLog.status}
                          textSize="text-[11px]"
                          width="w-auto"
                          className="px-2.5 py-1"
                        />
                        <i className="fa-regular fa-arrow-right-long text-gray-300 text-[10px]" />
                        <WorkOrderStatus
                          status={workOrderLog.newStatus}
                          textSize="text-[11px]"
                          width="w-auto"
                          className="px-2.5 py-1"
                        />
                      </>
                    ) : (
                      <WorkOrderStatus
                        status={displayStatus}
                        textSize="text-[11px]"
                        width="w-auto"
                        className="px-2.5 py-1"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            <div
              role={performedById ? "button" : undefined}
              tabIndex={performedById ? 0 : undefined}
              onClick={
                performedById ? () => navigate(`/user/details/profile/${performedById}`) : undefined
              }
              onKeyDown={
                performedById
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/user/details/profile/${performedById}`);
                      }
                    }
                  : undefined
              }
              className={`group flex items-center gap-2.5 border-t border-gray-100 bg-white/70 px-4 py-3 ${
                performedById ? "cursor-pointer hover:bg-gray-50 transition-colors" : ""
              }`}
            >
              <Avatar
                src={workOrderLog?.performedBy?.url || "/default-avatar.png"}
                alt={workOrderLog?.performedBy?.name}
                sx={{ width: 32, height: 32 }}
              />
              <div className="min-w-0">
                <p
                  className={`text-sm text-gray-800 leading-tight truncate ${
                    performedById ? "group-hover:text-primary group-hover:underline" : ""
                  }`}
                >
                  {workOrderLog?.performedBy?.name || "-"}
                </p>
                <p className="text-xs text-gray-500 leading-tight mt-0.5">
                  {workOrderLog?.performedBy?.designation || "—"}
                </p>
              </div>
              {performedById && (
                <i className="fa-light fa-arrow-up-right-from-square ml-auto text-xs text-gray-300 group-hover:text-primary" />
              )}
            </div>
          </div>

          {offerTimeline.length > 0 && (
            <div className="mt-6">
              <SectionHeading icon="fa-timeline-arrow" label="Offer Timeline" />
              <OfferTimeline items={offerTimeline} />
            </div>
          )}

          {showChanges && (
            <div className="mt-6">
              <SectionHeading
                icon="fa-pen-line"
                label="Changes"
                trailing={
                  changes.length > 0
                    ? `${changes.length} field${changes.length !== 1 ? "s" : ""}`
                    : null
                }
              />

              {changes.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 py-10 text-gray-400 gap-2">
                  <i className="fa-light fa-file-dashed-line text-2xl" />
                  <p className="text-sm">No field changes recorded.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {changeSections.map((group) => (
                    <div key={group.section}>
                      <div className="mb-2 flex items-center gap-2">
                        <p className="text-[11px] uppercase tracking-wider text-gray-400">
                          {group.section}
                        </p>
                        <span className="h-px flex-1 bg-gray-100" />
                      </div>
                      <div className="space-y-2.5">
                        {group.items.map((item, i) => (
                          <ChangeRow key={i} item={item} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </BaseDrawer>
  );
};

export default WorkOrderHistoryLog;

const OFFER_ACTION_META = {
  "Offer Created": { icon: "fa-clock", color: "text-gray-500", dot: "bg-gray-400" },
  Requested: { icon: "fa-hand", color: "text-[#9C27B0]", dot: "bg-[#9C27B0]" },
  "Counter Offer": { icon: "fa-arrows-rotate", color: "text-[#B58100]", dot: "bg-[#FFC107]" },
  "Routed to Engineer": { icon: "fa-route", color: "text-[#2196F3]", dot: "bg-[#2196F3]" },
  "Accepted Routed Offer": { icon: "fa-check", color: "text-[#5F8E2C]", dot: "bg-[#71A534]" },
  Assigned: { icon: "fa-user-check", color: "text-[#2561A9]", dot: "bg-[#2561A9]" },
  Unassigned: { icon: "fa-user-xmark", color: "text-gray-500", dot: "bg-gray-400" },
  Withdrawn: { icon: "fa-arrow-turn-down-left", color: "text-[#C56E00]", dot: "bg-[#FF9800]" },
  Rejected: { icon: "fa-xmark", color: "text-[#F44336]", dot: "bg-[#F44336]" },
  Declined: { icon: "fa-ban", color: "text-[#F44336]", dot: "bg-[#F44336]" },
  Cancelled: { icon: "fa-circle-xmark", color: "text-[#F44336]", dot: "bg-[#F44336]" },
};

const getOfferActionMeta = (action) =>
  OFFER_ACTION_META[action] || {
    icon: "fa-circle-dot",
    color: "text-gray-600",
    dot: "bg-gray-400",
  };

const RATE_DISPLAY = [
  { key: "hourlyRate", label: "Hourly" },
  { key: "halfDayRate", label: "Half Day" },
  { key: "fullDayRate", label: "Full Day" },
  { key: "fixedAmount", label: "Fixed" },
  { key: "additionalHourlyRate", label: "Add'l Hourly" },
  { key: "minimumHours", label: "Min Hours" },
];

const isSetRate = (value) =>
  value !== null && value !== undefined && value !== "" && Number(value) !== 0;

const RATE_BEARING_ACTIONS = new Set([
  "Requested",
  "Counter Offer",
  "Routed to Engineer",
  "Withdrawn",
  "Assigned",
]);

const OfferTimeline = ({ items }) => {
  const firstStepIdx = items.findIndex((it) => it.action !== "Offer Created");

  return (
    <div className="relative">
      {items.map((item, i) => (
        <OfferTimelineItem
          key={item.id ?? i}
          item={item}
          isFirst={i === firstStepIdx}
          isLast={i === items.length - 1}
        />
      ))}
    </div>
  );
};

const OfferTimelineItem = ({ item, isFirst, isLast }) => {
  const meta = getOfferActionMeta(item.action);
  const currency = item.currency ? CurrencySymbol[item.currency] || item.currency : "";
  const rates = RATE_DISPLAY.map((r) => ({ label: r.label, value: item.rates?.[r.key] })).filter(
    (r) => isSetRate(r.value),
  );
  const expenses = (item.expenses || []).filter((e) => isSetRate(e.amount));

  const showRates =
    rates.length > 0 &&
    (isFirst || RATE_BEARING_ACTIONS.has(item.action) || item.rateChanges?.length > 0);

  return (
    <div className="relative flex gap-3 pb-3 last:pb-0">
      {!isLast && <span className="absolute left-[15px] top-8 bottom-0 w-px bg-gray-200" />}
      <div
        className={`relative z-10 mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-white ${meta.dot} bg-opacity-15`}
      >
        <i className={`fa-solid ${meta.icon} text-xs ${meta.color}`} />
      </div>

      <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-sm font-normal ${meta.color}`}>{item.action}</p>
          <span className="text-[11px] text-gray-400 whitespace-nowrap">
            {formatDate(item.createdAt)} · {formattedTime(item.createdAt)}
          </span>
        </div>

        {(item.performedBy || item.onBehalfOf || item.assignedTo) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 pb-1.5">
            <Participant label="by" user={item.performedBy} />
            <Participant label="for" user={item.onBehalfOf} />
            <Participant label="to" user={item.assignedTo} />
          </div>
        )}

        {showRates && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {rates.map((r) => (
              <span
                key={r.label}
                className="rounded-md bg-gray-50 border border-gray-100 px-2 py-1 text-[11px] text-gray-500"
              >
                {r.label} <span className="font-normal text-gray-800">{r.value}</span>
              </span>
            ))}
            {currency && <span className="text-[11px] text-gray-400">{currency}</span>}
          </div>
        )}

        {item.rateChanges?.length > 0 && (
          <div className="mt-2 flex flex-col gap-1 rounded-lg bg-amber-50/60 px-2.5 py-2">
            {item.rateChanges.map((rc, idx) => (
              <span
                key={idx}
                className="text-[11px] text-amber-800 inline-flex items-center gap-1.5"
              >
                <i className="fa-solid fa-pen text-[9px] text-amber-500" />
                <span className="text-gray-600">{rc.field}</span>
                <span className="line-through text-gray-400">{rc.from ?? "—"}</span>
                <i className="fa-solid fa-arrow-right text-[8px] text-gray-400" />
                <span className="font-normal">{rc.to ?? "—"}</span>
              </span>
            ))}
          </div>
        )}

        {expenses.length > 0 && (
          <div className="mt-2 rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-2">
            <p className="text-[10px] font-normal text-gray-400 uppercase tracking-wider">
              Expenses
            </p>
            <div className="mt-1 flex flex-col gap-1">
              {expenses.map((e, idx) => (
                <div key={idx} className="flex items-baseline justify-between gap-3 text-[11px]">
                  <span
                    className={`flex items-baseline gap-1.5 min-w-0 ${
                      e.isDeleted ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {e.isDeleted ? (
                      <i
                        className="fa-solid fa-minus shrink-0 text-[8px] text-gray-400"
                        title="Removed at this step"
                      />
                    ) : (
                      e.isNew && (
                        <i
                          className="fa-solid fa-plus shrink-0 text-[8px] text-green-600"
                          title="Added at this step"
                        />
                      )
                    )}
                    <span className={`truncate ${e.isDeleted ? "line-through" : ""}`}>
                      {e.label || e.expenseType}
                      {e.description ? ` (${e.description})` : ""}
                    </span>
                    {e.addedByClient && (
                      <span
                        className="shrink-0 rounded border border-gray-200 bg-white px-1 py-px text-[9px] uppercase tracking-wide text-gray-400"
                        title="Added by the client, not part of the offer negotiation"
                      >
                        Client
                      </span>
                    )}
                  </span>
                  <span
                    className={`font-normal whitespace-nowrap ${
                      e.isDeleted ? "text-gray-400 line-through" : "text-gray-800"
                    }`}
                  >
                    {currency}
                    {e.amount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const STATUS_FIELDS = ["action", "customer status", "status"];

const isFile = (value) => {
  if (Array.isArray(value)) return value.length > 0 && value[0]?.fileName !== undefined;
  return value !== null && typeof value === "object" && value?.fileName !== undefined;
};

const FileLinks = ({ value }) => {
  const files = Array.isArray(value) ? value : [value];
  return (
    <div className="flex flex-col gap-1">
      {files.map((file, i) =>
        file?.url ? (
          <a
            key={i}
            href={file.url}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline font-light truncate max-w-[180px]"
          >
            {file.fileName || file.url}
          </a>
        ) : (
          <p key={i} className="font-light text-gray-600 truncate max-w-[180px]">
            {file?.fileName || "-"}
          </p>
        ),
      )}
    </div>
  );
};

const LIST_SECTIONS = new Set(["Eligibility", "Services"]);

const parseListValue = (text) => {
  if (typeof text !== "string" || !text.trim() || text === "None" || text === "-") return [];
  return text
    .split(/,?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separator = line.indexOf(": ");
      const hasGroup = separator > -1;
      const group = hasGroup ? line.slice(0, separator).trim() : null;
      const rest = hasGroup ? line.slice(separator + 2) : line;
      return {
        group,
        items: rest
          .split(", ")
          .map((i) => i.trim())
          .filter(Boolean),
      };
    })
    .filter((g) => g.items.length > 0);
};

const ListValue = ({ groups, strikethrough }) => (
  <div className="flex min-w-0 flex-col gap-2">
    {groups.map((group, i) => (
      <div key={i}>
        {group.group && (
          <p className="mb-1 text-[10px] uppercase tracking-wide text-gray-400">{group.group}</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {group.items.map((item, j) => (
            <span
              key={j}
              className={`rounded-md border px-2 py-0.5 text-xs ${
                strikethrough
                  ? "border-gray-100 bg-gray-50 text-gray-400 line-through"
                  : "border-gray-200 bg-white text-gray-700"
              }`}
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const isUserRef = (value) =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  value.userId !== undefined &&
  value.name !== undefined;

const UserValue = ({ user, strikethrough, compact }) => {
  const navigate = useNavigate();
  const goToProfile = () => navigate(`/user/details/profile/${user.userId}`);
  const avatarSize = compact ? 22 : 24;

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={goToProfile}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          goToProfile();
        }
      }}
      className={`group inline-flex max-w-full min-w-0 cursor-pointer items-center rounded-full align-middle transition-colors ${
        compact ? "gap-1.5" : "gap-1.5 border py-0.5 pl-0.5 pr-2.5"
      } ${
        compact
          ? "hover:opacity-80"
          : strikethrough
            ? "border-gray-100 bg-gray-50 hover:bg-gray-100"
            : "border-gray-200 bg-white hover:border-primary/40 hover:bg-blue-50/40"
      }`}
    >
      <Avatar
        src={user.url || "/default-avatar.png"}
        alt={user.name}
        sx={{
          width: avatarSize,
          height: avatarSize,
          fontSize: 9,
          flexShrink: 0,
          ...(strikethrough && { filter: "grayscale(1)", opacity: 0.55 }),
        }}
      />
      <span className="flex min-w-0 flex-col">
        <span
          className={`truncate text-xs leading-tight ${
            strikethrough ? "text-gray-400 line-through" : "text-gray-700 group-hover:text-primary"
          }`}
        >
          {user.name}
        </span>
        {user.role && !strikethrough && (
          <span className="truncate text-[10px] leading-tight text-gray-400">{user.role}</span>
        )}
      </span>
    </span>
  );
};

const Participant = ({ label, user }) => {
  if (!user) return null;

  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span className="shrink-0 text-[11px] leading-4 text-gray-400">{label}</span>
      {isUserRef(user) ? (
        <UserValue user={user} compact />
      ) : (
        <span className="truncate text-xs leading-4 text-gray-600">{user}</span>
      )}
    </span>
  );
};

const ChangeValue = ({ raw, formatted, strikethrough, section }) => {
  if (isUserRef(raw)) return <UserValue user={raw} strikethrough={strikethrough} />;

  if (isFile(raw)) return <FileLinks value={raw} />;

  if (LIST_SECTIONS.has(section)) {
    const groups = parseListValue(formatted);
    if (groups.length > 0) return <ListValue groups={groups} strikethrough={strikethrough} />;
  }

  return (
    <p
      className={`font-light min-w-0 break-words ${
        strikethrough ? "line-through text-gray-400" : "text-gray-700"
      }`}
      dangerouslySetInnerHTML={{ __html: formatted }}
    />
  );
};

const ChangeRow = ({ item }) => {
  const isStatusField = STATUS_FIELDS.includes(item.field?.toLowerCase());

  // Prefer resolved labels from the backend; fall back to raw values for formatting
  const displayFrom = item.fromLabel ?? item.from;
  const displayTo = item.toLabel ?? item.to;

  const oldFormatted = isStatusField
    ? item.from
    : formatValue(item.fieldLabel || item.field, displayFrom);
  const newFormatted = isStatusField
    ? item.to
    : formatValue(item.fieldLabel || item.field, displayTo);

  const hasChanged = oldFormatted !== newFormatted;

  const oldCell = isStatusField ? (
    item.from ? (
      <WorkOrderStatus className="py-1" status={item.from} />
    ) : (
      <p className="font-light text-gray-400 text-sm">—</p>
    )
  ) : (
    <ChangeValue
      raw={displayFrom}
      formatted={oldFormatted}
      strikethrough={hasChanged}
      section={item.section}
    />
  );

  const newCell = isStatusField ? (
    item.to ? (
      <WorkOrderStatus className="py-1" status={item.to} />
    ) : (
      <p className="font-light text-gray-400 text-sm">—</p>
    )
  ) : (
    <ChangeValue
      raw={displayTo}
      formatted={newFormatted}
      strikethrough={false}
      section={item.section}
    />
  );

  const withTooltip = (raw, formatted, child) =>
    isFile(raw) ? (
      child
    ) : (
      <ValueWithTooltip field={item.field} value={formatted}>
        {child}
      </ValueWithTooltip>
    );

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
        <p className="text-xs font-normal text-gray-600">{item.fieldLabel || item.field}</p>
      </div>

      {isStatusField ? (
        <div className="flex items-center gap-3 px-4 py-3 text-sm">
          {oldCell}
          <i className="fa-solid fa-arrow-right text-gray-300 text-xs shrink-0" />
          {newCell}
        </div>
      ) : (
        <div className="divide-y divide-gray-50 text-sm">
          <div className="grid grid-cols-[4rem_1fr] items-start gap-x-3 px-4 py-2.5">
            <span className="mt-[2px] text-[11px] uppercase tracking-wide text-gray-400">
              Before
            </span>
            {withTooltip(item.from, oldFormatted, oldCell)}
          </div>
          <div className="grid grid-cols-[4rem_1fr] items-start gap-x-3 bg-blue-50/40 px-4 py-2.5">
            <span className="mt-[2px] text-[11px] uppercase tracking-wide text-primary">After</span>
            {withTooltip(item.to, newFormatted, newCell)}
          </div>
        </div>
      )}
    </div>
  );
};

const getPlainText = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return div.textContent || div.innerText || "";
};

const ValueWithTooltip = ({ value, children, field }) => {
  const isDescriptionNotesField =
    field?.toLowerCase() === "description" || field?.toLowerCase() === "notes";

  if (!isDescriptionNotesField) return children;

  const truncated = truncateHTML(value);
  const fullText = getPlainText(value);
  const truncatedText = getPlainText(truncated);
  const isTruncated = fullText.length > truncatedText.length;

  if (!isTruncated) return children;

  return (
    <BaseTooltip
      placement="right"
      interactive
      content={
        <div className="text-white text-xs">
          <div dangerouslySetInnerHTML={{ __html: value }} />
        </div>
      }
    >
      <div dangerouslySetInnerHTML={{ __html: truncated }} />
    </BaseTooltip>
  );
};
